/**
 * Hook for schedule CSV import/export
 *
 * Provides hooks to download and import schedule CSV files
 * for collection request target management.
 */

import { useState, useCallback } from 'react'
import type { ScheduleImportResult } from '../types/collection-targets'
import { downloadSchedule, importSchedule } from '../services/collection-targets'

// =============================================================================
// useScheduleImport
// =============================================================================

interface UseScheduleImportResult {
  importing: boolean
  downloading: boolean
  result: ScheduleImportResult | null
  error: string | null
  importCsv: (caseId: string, requestId: string, file: File) => Promise<ScheduleImportResult | null>
  downloadCsv: (caseId: string, requestId: string) => Promise<boolean>
  clearResult: () => void
  clearError: () => void
}

/**
 * Hook for schedule CSV import and export operations
 */
export function useScheduleImport(): UseScheduleImportResult {
  const [importing, setImporting] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [result, setResult] = useState<ScheduleImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const importCsv = useCallback(
    async (caseId: string, requestId: string, file: File): Promise<ScheduleImportResult | null> => {
      setImporting(true)
      setError(null)
      setResult(null)

      try {
        const importResult = await importSchedule(caseId, requestId, file)
        setResult(importResult)
        return importResult
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to import schedule CSV')
        return null
      } finally {
        setImporting(false)
      }
    },
    []
  )

  const downloadCsv = useCallback(async (caseId: string, requestId: string): Promise<boolean> => {
    setDownloading(true)
    setError(null)

    try {
      await downloadSchedule(caseId, requestId)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download schedule CSV')
      return false
    } finally {
      setDownloading(false)
    }
  }, [])

  const clearResult = useCallback(() => {
    setResult(null)
  }, [])
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    importing,
    downloading,
    result,
    error,
    importCsv,
    downloadCsv,
    clearResult,
    clearError,
  }
}
